import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouterState } from '@tanstack/react-router';
import { TopNavShell } from '@noor/ui';
import { EATS_BOTTOM_NAV } from '../config/nav';
import { eatsScreenForPath, isEatsNavActive } from '../lib/eats-nav-state';
import { MobileNavBarSlotProvider } from '../lib/mobile-nav-bar-slot';
import { EatsAccountMenu } from './eats-account-menu';

interface EatsShellLayoutMobileProps {
  children: ReactNode;
}

/**
 * The Eats chrome on mobile (Figma node 26249:137938).
 *
 * A different shell from the desktop one, not a narrow rendering of it: the
 * brand lockup, tab nav, app switcher and support button are all replaced by a
 * screen-titled nav bar with a back affordance, and the tab bar below carries
 * labels. Only one of the two mounts (`useIsMobile` in the root layout), so
 * this file owns the mobile tree outright.
 *
 * The account menu keeps its place in the nav bar's trailing slot — it is the
 * only route to theme, language, profile and sign-out on this breakpoint, all
 * of which the desktop header exposes as separate controls.
 *
 * The nav bar itself is DEFAULT content: most screens are happy with the
 * sub-app/section title pair from `eatsScreenForPath`, but a page that needs
 * something the shell can't derive on its own — the Menu screen's live branch
 * name and picker — claims the bar via `useMobileNavBarOverride` instead. See
 * `mobile-nav-bar-slot.tsx` for why this is a context rather than a prop.
 */
export function EatsShellLayoutMobile({ children }: EatsShellLayoutMobileProps) {
  const { t } = useTranslation('eats');
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const screen = eatsScreenForPath(pathname);

  return (
    <MobileNavBarSlotProvider>
      {(override) => (
        <TopNavShell.Root className="h-dvh bg-card">
          <TopNavShell.MobileNavBar
            title={override?.title ?? t(screen.titleKey)}
            // `!== undefined`, not `??` — the same three-state rule as
            // `trailing` below: an override that OMITS `subtitle` keeps the
            // shell's default, while an explicit `null` renders none (the
            // users screen's Figma frame is a title alone). `??` collapsed
            // those two cases and always restored the default.
            subtitle={
              override?.subtitle !== undefined ? override.subtitle : t(screen.subtitleKey)
            }
            leading={
              override?.onBack ? (
                // The PAGE's own handler — a screen that claims the bar owns
                // what "back" means there (a wizard step, a drill-down).
                // Calling a shell-local handler here instead would navigate
                // away and silently discard what the page asked for.
                <TopNavShell.MobileNavBar.Back
                  onClick={override.onBack}
                  aria-label={t('common:actions.back')}
                />
              ) : (
                // Back leaves the sub-app for the launcher, matching what the
                // wordmark does on desktop — the first tab already covers
                // "home within Eats", so a history-based back would just
                // duplicate it.
                <TopNavShell.MobileNavBar.Back
                  asChild
                  aria-label={t('common:actions.back')}
                >
                  <Link to="/welcome" />
                </TopNavShell.MobileNavBar.Back>
              )
            }
            trailing={
              override?.trailing !== undefined ? (
                override.trailing
              ) : (
                <div className="flex items-center gap-2">
                  <EatsAccountMenu className="size-10" />
                </div>
              )
            }
          />

          <TopNavShell.Main data-transition="page">{children}</TopNavShell.Main>

          <TopNavShell.BottomNav>
            {EATS_BOTTOM_NAV.map((item) => {
              const Icon = item.mobileIcon;
              return (
                <TopNavShell.BottomNavItem
                  key={item.path}
                  asChild
                  // No `aria-label` here, unlike the desktop icon-only bar: the
                  // tab renders its label as text, and a label would shadow it.
                  active={isEatsNavActive(item.path, pathname)}
                >
                  <Link to={item.path}>
                    <Icon className="size-6" />
                    <span>{t(item.tabLabelKey)}</span>
                  </Link>
                </TopNavShell.BottomNavItem>
              );
            })}
          </TopNavShell.BottomNav>
        </TopNavShell.Root>
      )}
    </MobileNavBarSlotProvider>
  );
}
